import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/InventoryManager.jsx
// COMPLETE NEW FILE - Sprint 1.5
// Pantry Inventory Management - Expiry Tracking, Freeze/Unfreeze, Depletion
// Patent claims exercised: P-002 Claims 1-2 (pantry inventory tracking)
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useConfirm } from './ConfirmProvider';

const InventoryManager = ({
  token,
  pantryId = null,
  onRefreshPantry,
  showAsCard = false
}) => {
  const confirm = useConfirm();
  // State
  const [expiringItems, setExpiringItems] = useState([]);
  const [expiredItems, setExpiredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysAhead, setDaysAhead] = useState(7);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  // Load expiring items on mount and when daysAhead changes
  useEffect(() => {
    loadExpiringItems();
  }, [daysAhead, pantryId]);

  const loadExpiringItems = async () => {
    setLoading(true);
    setError('');

    try {
      const url = pantryId
        ? `${API_BASE}/api/pantry/user/expiring-items?days=${daysAhead}&pantry_id=${pantryId}`
        : `${API_BASE}/api/pantry/user/expiring-items?days=${daysAhead}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load expiring items');
      }

      const data = await response.json();
      setExpiringItems(data.expiring_soon || []);
      setExpiredItems(data.expired || []);

    } catch (err) {
      console.error('Error loading expiring items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const freezeItem = async (itemId) => {
    setActionInProgress(itemId);

    try {
      const response = await fetch(`${API_BASE}/api/pantry/items/${itemId}/freeze`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to freeze item');
      }

      const data = await response.json();
      // Show freeze result
      if (data.freeze_warning) {
        alert(data.freeze_warning);
      } else {
        alert(`${data.item_name || 'Item'} frozen successfully. New expiry: ${data.new_expiry || 'extended'}`);
      }

      // Refresh list
      await loadExpiringItems();
      if (onRefreshPantry) onRefreshPantry();

    } catch (err) {
      alert(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const unfreezeItem = async (itemId) => {
    setActionInProgress(itemId);

    try {
      const response = await fetch(`${API_BASE}/api/pantry/items/${itemId}/unfreeze`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unfreeze item');
      }

      const data = await response.json();
      alert(`${data.item_name} thawed. Use within ${data.days_to_use} days.`);

      // Refresh list
      await loadExpiringItems();
      if (onRefreshPantry) onRefreshPantry();

    } catch (err) {
      alert(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const deleteExpiredItems = async () => {
    if (!(await confirm({
      title: 'Delete expired items?',
      message: `${expiredItems.length} expired item${expiredItems.length === 1 ? '' : 's'} will be removed from your pantry. This cannot be undone.`,
      confirmLabel: `Delete ${expiredItems.length} Item${expiredItems.length === 1 ? '' : 's'}`,
      destructive: true,
    }))) {
      return;
    }

    try {
      const url = pantryId
        ? `${API_BASE}/api/pantry/user/expired-items?pantry_id=${pantryId}`
        : `${API_BASE}/api/pantry/user/expired-items`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete expired items');
      }

      const data = await response.json();
      alert(`Deleted ${data.deleted_count} expired item(s)`);

      // Refresh
      await loadExpiringItems();
      if (onRefreshPantry) onRefreshPantry();

    } catch (err) {
      alert(err.message);
    }
  };

  const getExpiryUrgency = (daysUntil) => {
    if (daysUntil <= 1) return { color: '#DC2626', bg: '#FEE2E2', label: 'Tomorrow!' };
    if (daysUntil <= 3) return { color: '#D97706', bg: '#FEF3C7', label: `${daysUntil} days` };
    return { color: '#059669', bg: '#D1FAE5', label: `${daysUntil} days` };
  };

  if (loading) {
    return (
      <div className={showAsCard ? 'im-container im-container--card' : 'im-container'}>
        <div className="im-card">
          <div className="im-loading">
            Loading inventory status...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={showAsCard ? 'im-container im-container--card' : 'im-container'}>
      <div className="im-card">
        <div className="im-header">
          <h3 className="im-title">
            {'\u23F0'} Expiring Soon
            {expiringItems.length > 0 && (
              <span className="im-countBadge">
                {expiringItems.length}
              </span>
            )}
          </h3>
          <button
            onClick={loadExpiringItems}
            className="im-refreshButton"
          >
            {'\u{1F504}'} Refresh
          </button>
        </div>

        {/* Summary */}
        <div className="im-summary">
          <div className="im-summaryCard">
            <div className="im-summaryValue im-summaryValue--expiring">
              {expiringItems.length}
            </div>
            <div className="im-summaryLabel">Expiring in {daysAhead} days</div>
          </div>
          <div className="im-summaryCard">
            <div className="im-summaryValue im-summaryValue--expired">
              {expiredItems.length}
            </div>
            <div className="im-summaryLabel">Already expired</div>
          </div>
        </div>

        {/* Filter by days */}
        <div className="im-filterRow">
          <span className="im-filterLabel">Show items expiring in:</span>
          {[3, 7, 14, 30].map(days => (
            <button
              key={days}
              className={daysAhead === days ? 'im-filterButton im-filterButton--active' : 'im-filterButton'}
              onClick={() => setDaysAhead(days)}
            >
              {days} days
            </button>
          ))}
        </div>

        {/* Expiring Items List */}
        {expiringItems.length === 0 ? (
          <div className="im-emptyState">
            <div className="im-emptyIcon">{'\u2705'}</div>
            <p>No items expiring in the next {daysAhead} days!</p>
          </div>
        ) : (
          <div className="im-itemList">
            {expiringItems.map(item => {
              const urgency = getExpiryUrgency(item.days_until_expiry);

              return (
                <div key={item.id} className="im-itemCard">
                  <div className="im-itemInfo">
                    <div className="im-itemName">
                      {item.name}
                      {item.storage_method && (
                        <span className="im-storageBadge">
                          {item.storage_method === 'pantry' ? 'Pantry' : item.storage_method === 'freezer' ? 'Freezer' : 'Refrigerator'}
                        </span>
                      )}
                      {item.is_frozen && (
                        <span className="im-frozenBadge">
                          {'\u2744\uFE0F'} Frozen
                        </span>
                      )}
                      {item.is_opened && (
                        <span className="im-openedBadge">
                          Opened
                        </span>
                      )}
                    </div>
                    <div className="im-itemMeta">
                      {item.quantity} {item.unit} {'\u2022'} Expires: {item.expiration_date}
                    </div>
                  </div>

                  <div className="im-itemActions">
                    <span
                      className="im-urgencyBadge"
                      style={{ backgroundColor: urgency.bg, color: urgency.color }}
                    >
                      {urgency.label}
                    </span>

                    {item.is_frozen ? (
                      <button
                        className="im-actionButton im-actionButton--unfreeze"
                        onClick={() => unfreezeItem(item.id)}
                        disabled={actionInProgress === item.id}
                      >
                        {actionInProgress === item.id ? '...' : '\u{1F525} Thaw'}
                      </button>
                    ) : (
                      <button
                        className="im-actionButton im-actionButton--freeze"
                        onClick={() => freezeItem(item.id)}
                        disabled={actionInProgress === item.id}
                      >
                        {actionInProgress === item.id ? '...' : '\u2744\uFE0F Freeze'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Expired Items Section */}
        {expiredItems.length > 0 && (
          <div className="im-expiredSection">
            <div className="im-expiredHeader">
              <h4
                className="im-expiredTitle"
                onClick={() => setShowExpired(!showExpired)}
              >
                {'\u26A0\uFE0F'} Expired Items ({expiredItems.length})
                <span className="im-expandIcon">{showExpired ? '\u25BC' : '\u25B6'}</span>
              </h4>
              <button
                className="im-actionButton im-actionButton--delete"
                onClick={deleteExpiredItems}
              >
                {'\u{1F5D1}\uFE0F'} Delete All Expired
              </button>
            </div>

            {showExpired && (
              <div className="im-itemList">
                {expiredItems.map(item => (
                  <div key={item.id} className="im-itemCard im-itemCard--expired">
                    <div className="im-itemInfo">
                      <div className="im-itemName">
                        {item.name}
                        {item.storage_method && (
                          <span className="im-storageBadge">
                            {item.storage_method === 'pantry' ? 'Pantry' : item.storage_method === 'freezer' ? 'Freezer' : 'Refrigerator'}
                          </span>
                        )}
                        {item.is_opened && (
                          <span className="im-openedBadge">
                            Opened
                          </span>
                        )}
                      </div>
                      <div className="im-itemMeta">
                        {item.quantity} {item.unit} {'\u2022'} Expired: {item.expiration_date}
                        <span className="im-daysAgo">
                          ({item.days_past_expiry} days ago)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryManager;
